import React, { useState, useEffect, useRef } from "react";
import { data, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from '@app/store';
import {
  fetchUserById,
  createUser,
  updateUser,
  UserActions,
} from "./UserSlice";
import { fetchAffiliates } from "../affiliate/AffiliateSlice";
import { fetchUserProfile } from "../auth/authSlice";
import { UserMapper } from "./UserMapper";
import { UserOutputDTO, UserRoleLabels, UserStatusLabels, UserRole, UserStatus } from './types';
import { AffiliateOutputDTO } from "../affiliate/types";
import { DataForm } from '@components/ui';
import { Field } from '@components/ui/DataForm';
import { FormSkeleton } from '@components/ui/FormSkeleton';
import { ApiError } from '@/types/api';
import { useNotification } from '@hooks/useNotification';
import { Box, Typography, Alert } from "@mui/material";
import { useAuth } from '@hooks/useAuth';

async function readFileAsDataURL(file:any) {
    let result_base64 = await new Promise((resolve) => {
        let fileReader = new FileReader();
        fileReader.onload = (e) => resolve(fileReader.result);
        fileReader.readAsDataURL(file);
    });

    console.log(result_base64); // aGV5IHRoZXJl...

    return result_base64;
}

const UserForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentItem, loading } = useSelector(
    (state: RootState) => state.user
  );
  const { items: affiliates, loading: affiliatesLoading } = useSelector(
    (state: RootState) => state.affiliate
  );
  
  // Get current user info from auth state
  const { user, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState<Partial<UserOutputDTO>>({});
  const [formError, setFormError] = useState<ApiError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const notification = useNotification();

  const isLoadingRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  const submitAttemptRef = useRef(false);
  const ignoreAreaChange = useRef(true);
  const ignoreRoleChange = useRef(true);

  const ignoreSectorChange = useRef(true);


  // Make sure we have the current user data
  useEffect(() => {
    if (isAuthenticated && !user) {
      console.log("🔄 Fetching current user profile");
      dispatch(fetchUserProfile());
    }
  }, [dispatch, isAuthenticated, user]);

  // Debug output for current user
  useEffect(() => {
    console.log("🧪 Current authenticated user:", user);
  }, [user]);

  // User role-based access control
  const currentUserRole = user?.role as UserRole;
  
  // Redirect lawyer users away from this form
  useEffect(() => {
    if (currentUserRole === UserRole.LAWYER) {
      notification.showError("Você não tem permissão para acessar esta página");
      navigate("/");
    }
  }, [currentUserRole, navigate, notification]);

  useEffect(() => {
    const loadData = async () => {
      if (isLoadingRef.current || initialLoadDoneRef.current) {
        return;
      }

      try {
        console.log("📡 Iniciando carregamento do Usuário:", id);
        isLoadingRef.current = true;

        // Load affiliates
        console.log("📡 UserForm - Carregando Afiliados");
        await dispatch(fetchAffiliates({}));

        if (id) {
          console.log("📡 UserForm - Carregando Usuário:", id);
          const resultAction = await dispatch(fetchUserById(id));

          if (fetchUserById.fulfilled.match(resultAction)) {
            console.log("✅ Usuário carregado com sucesso");
          } else {
            throw new Error("Erro ao carregar dados");
          }
        }
      } catch (error) {
        console.error("❌ Erro ao carregar Usuário:", error);
        notification.showError("Erro ao carregar dados");
        setFormError({
          message: "Erro ao carregar dados",
          code: "LOAD_ERROR",
          statusCode: 500,
        });
      } finally {
        isLoadingRef.current = false;
        initialLoadDoneRef.current = true;
        setIsInitialLoad(false);
      }
    };

    loadData();

    return () => {
      console.log("🧹 Limpando estado do formulário");
      dispatch(UserActions.clearCurrentItem());
    };
  }, []);
  

  useEffect(() => {
    if (currentItem) {
      console.log("📝 User - Atualizando dados do formulário:", currentItem);
      ignoreSectorChange.current=true;
      ignoreAreaChange.current=true;
      ignoreRoleChange.current=true;

      let role = null;
      if(currentItem.enterpriseId && affiliates.length>0 && currentItem.role){
      role = affiliates.filter((item)=>item._id==currentItem.enterpriseId)[0]?.roles?.filter((item:any)=>item._id==currentItem.role)[0];
      console.log('role update',role)
    }

      setFormData({
        role_name: role?.name ?? '',
        name: currentItem.name,
        email: currentItem.email,
        status: currentItem.status==UserStatus.ACTIVE?false:true,
        role: currentItem.role,
        phases: currentItem.phases,
        city: currentItem.city ?? '',
        street: currentItem.street ?? '',
        number: currentItem.number ?? '',
        neighborhood: currentItem.neighborhood ?? '',
        state: currentItem.state ?? '',
        postal: currentItem.postal ?? '',
        cpf_cnpj: currentItem.cpf_cnpj ?? '',
        whatsapp: currentItem.whatsapp ?? '',
        sector: currentItem.sector ?? '',
        area: currentItem.area ?? '',

        image: currentItem.photoUrl ?? '',



        enterpriseId: currentItem.enterpriseId,
      });
    }
  }, [currentItem]);

  useEffect(()=>{
    console.log('atualizou',fields)
    setFields(fields.map((item)=>{
      if(item.name=='role'){
        item.options=getRoleOptions();
      }
      else if(item.name=='sector'){
        item.options=getSectorOptions();
      }
      else if(item.name=='area'){
        item.options=getAreaOptions();
      }
      else if(item.name=='enterpriseId'){
        item.options=affiliates.map((affiliate: AffiliateOutputDTO) => ({
        value: affiliate._id || "",
        label: affiliate.name || "Sem nome",
      }))
      }
      return item;
    }))
    // if(formData.enterpriseId && affiliates.length>0){
    //   const role = affiliates.filter((item)=>item._id==formData.enterpriseId)[0]?.roles?.filter((item:any)=>item._id==formData.role)[0];
    //   console.log('role update',role)
    //   setFormData(prev => ({ ...prev, role_name: role?.name ?? '' }))
    // }

  },[formData.enterpriseId,affiliates,formData.sector,formData.area,formData.role])

  useEffect(()=>{
    console.log('Change',ignoreSectorChange.current)
    if(ignoreSectorChange.current!=true){
      let forms = {...formData};
      forms.area = '';
      forms.role='';
      forms.role_name='';
      forms.zerararea = Date.now();

      setFormData(forms);
    }
    ignoreSectorChange.current=false
  },[formData.sector])

  useEffect(()=>{
    if(ignoreRoleChange.current!=true){

    console.log('Change',ignoreSectorChange.current)
      const role = affiliates.filter((item)=>item._id==formData.enterpriseId)[0]?.roles?.filter((item:any)=>item._id==formData.role)[0];
      console.log('role update',role)
      setFormData(prev => ({ ...prev, role_name: role?.name ?? '' }))
    }
    ignoreRoleChange.current=false

  },[formData.role])

  useEffect(()=>{
    if(ignoreAreaChange.current!=true){

      let forms = {...formData};
      forms.role='';
      forms.role_name='';
      forms.zerarrole = Date.now();

      setFormData(forms);
      
    }
    ignoreAreaChange.current=false
  },[formData.area])

  const getSectorOptions = () => {
    if(formData.enterpriseId && affiliates.length>0){
      console.log('entrou aqui',formData.enterpriseId,affiliates)
      console.log(affiliates.filter((item)=>item._id==formData.enterpriseId))
      return affiliates.filter((item)=>item._id==formData.enterpriseId)[0]?.sectors?.map((item:any)=>({label:item.name,value:item._id}));
    }
    else return [];
   
  };

  const getAreaOptions = () => {
    if(formData.enterpriseId && affiliates.length>0 && formData.sector){
      console.log('entrou aqui',formData.enterpriseId,affiliates)
      console.log(affiliates.filter((item)=>item._id==formData.enterpriseId))
      let sector =  affiliates.filter((item)=>item._id==formData.enterpriseId)[0]?.sectors?.filter((item:any)=>item._id==formData.sector)[0];
      console.log(formData.sector,sector,affiliates.filter((item)=>item._id==formData.enterpriseId)[0]?.areas)
      let areas =  affiliates.filter((item)=>item._id==formData.enterpriseId)[0]?.areas?.filter((item:any)=>sector?.areas?.includes(item._id));
      console.log('areas result',areas)
      return areas.map((item:any)=>({label:item.name,value:item._id}));
    }
    else return [];
   
  };


  // Filter available role options based on current user's role
  const getRoleOptions = () => {
    if(formData.enterpriseId && affiliates.length>0 && formData.area){
      console.log('entrou aqui',formData.enterpriseId,affiliates)
      console.log(affiliates.filter((item)=>item._id==formData.enterpriseId))
      let area =  affiliates.filter((item)=>item._id==formData.enterpriseId)[0]?.areas?.filter((item:any)=>item._id==formData.area)[0];

      return affiliates.filter((item)=>item._id==formData.enterpriseId)[0]?.roles?.filter((item:any)=>area?.roles?.includes(item._id)).map((item:any)=>({label:item.name,value:item._id}));
    }
    else return [];
    

    const allRoles = Object.entries(UserRoleLabels).map(([value, label]) => ({
      value,
      label,
    }));

    // If current user is a Manager, exclude ADMIN role
    if (currentUserRole === UserRole.MANAGER) {
      return allRoles.filter(role => role.value !== UserRole.ADMIN);
    }

    return allRoles;
  };

  // Determine if enterprise_id field should be disabled
  const isEnterpriseIdDisabled = () => {
    // Only ADMIN can modify enterprise_id
    return currentUserRole !== UserRole.ADMIN && !!formData.enterpriseId;
  };
  const [fields,setFields] = useState<any>([
   
    {
      name: "image",
      label: "Imagem",
      type: "image",
      show:['name','role_name'],
      required: true,
      col:'48%',
      validationError: formError?.validationErrors?.name?.message,
    },
    {
      name: "status",
      label: "Bloquear Colaborador",
      type: "toogle",
      col:'48%',
      required: true,
      options: UserStatusLabels,
      validationError: formError?.validationErrors?.status?.message,
    },
     {
      name:'section1',
      type:'section',
      label:'Informações Gerais'
    },
     {
      name:'separator',
      type:'separator',
      label:'Informações Gerais'
    },
    {
      name: "name",
      label: "Nome/Razão Social",
      type: "text",
      required: true,
      col:'100%',
      validationError: formError?.validationErrors?.name?.message,
    },
    {
      name: "email",
      label: "Email",
      type: "text",
      required: true,
      col:'48%',
      validationError: formError?.validationErrors?.email?.message,
    },
    {
      name: "password",
      label: id ? "Senha" : "Senha",
      tooltip: id ? "(deixe em branco para manter a atual)":undefined,
      type: "password",
      required: !id,
      col:'48%',
      validationError: formError?.validationErrors?.password?.message,
    },
    {
      name: "cpf_cnpj",
      label: "CPF/CNPJ",
      type: "text",
      required: true,
      col:'100%',
      validationError: formError?.validationErrors?.cpf_cnpj?.message,
    },
    
    {
      name: "enterpriseId",
      label: "Empresa",
      type: "select",
      col:'48%',
      required: true,
      disabled: isEnterpriseIdDisabled(),
      options: affiliates.map((affiliate: AffiliateOutputDTO) => ({
        value: affiliate._id || "",
        label: affiliate.name || "Sem nome",
      })),
      validationError: formError?.validationErrors?.enterpriseId?.message,
    },
    
     {
      name: "sector",
      label: "Setor",
      type: "select",
      col:'48%',
      required: true,
      options: getSectorOptions(),
      // options:Object.entries({Geral:'Geral','Comercial':'Comercial'}).map(([value, label]) => ({
      //   value,
      //   label,
      // })),
      validationError: formError?.validationErrors?.sector?.message,
    },

     {
      name: "area",
      label: "Área",
      type: "select",
      col:'48%',
      required: true,
      options: getAreaOptions(),
      validationError: formError?.validationErrors?.area?.message,
    },



     {
      name: "role",
      label: "Perfil",
      type: "select",
      col:'48%',
      required: true,
      options: getRoleOptions(),
      validationError: formError?.validationErrors?.role?.message,
    },
    
     
     {
      name: "whatsapp",
      label: "Whatsapp",
      type: "text",
      col:'100%',
      required: true,
      validationError: formError?.validationErrors?.whatsapp?.message,
    },
   
    {
      name:'section',
      type:'section',

      label:'Endereço'
    },
     {
      name:'separator1',
      type:'separator',
      label:'Endereço'
    },
    {
      name: "postal",
      label: "CEP",
      type: "text",
      required: true,
      col:'100%',
      validationError: formError?.validationErrors?.postal?.message,
    },
    {
      name: "street",
      label: "Rua",
      type: "text",
      required: true,
      col:'48%',
      validationError: formError?.validationErrors?.street?.message,
    },
    {
      name: "number",
      label: "Número",
      type: "text",
      required: false,
      col:'48%',
      validationError: formError?.validationErrors?.number?.message,
    },

    {
      name: "neighborhood",
      label: "Bairro",
      type: "text",
      required: true,
      col:'100%',
      validationError: formError?.validationErrors?.neighborhood?.message,
    },
    {
      name: "city",
      label: "Cidade",
      type: "text",
      required: true,
      col:'48%',
      validationError: formError?.validationErrors?.city?.message,
    },
    {
      name: "state",
      label: "Estado (Sigla)",
      type: "text",
      required: true,
      col:'48%',
      validationError: formError?.validationErrors?.state?.message,
    },
   
  ]);

  const handleSubmit = async (data2: Partial<UserOutputDTO>) => {
    if (submitAttemptRef.current) {
      notification.showWarning("Submissão em andamento, aguarde...");
      return;
    }
    let data:any={...data2}

    try {
      submitAttemptRef.current = true;
      setIsSubmitting(true);
      setFormError(null);

      if(data?.image instanceof File){
        data.image = await readFileAsDataURL(data.image);
      }
      else{
        delete data.image;
      }

      // If password is empty in edit mode, remove it from the payload
      if (id && !data.password) {
        delete data.password;
      }

      if(data.status==true) data.status=UserStatus.INACTIVE;
      else data.status=UserStatus.ACTIVE;

      // If the user is a Manager trying to create an Admin, prevent it
      if (currentUserRole === UserRole.MANAGER && data.role === UserRole.ADMIN) {
        notification.showError("Você não tem permissão para criar usuários administradores");
        setIsSubmitting(false);
        submitAttemptRef.current = false;
        return;
      }

      // If the user is a Manager, keep the existing enterpriseId or set the current user's enterpriseId
      if (currentUserRole === UserRole.MANAGER) {
        if (id && formData.enterpriseId) {
          // Keep the original enterprise ID when editing
          data.enterpriseId = formData.enterpriseId;
        } else if (!id) {
          // Set the manager's own enterprise ID for new users
          data.enterpriseId = user?.enterpriseId;
        }
      }

      const mappedData = UserMapper.toDTO(data);
      console.log("📤 Enviando dados:", mappedData);

      const resultAction = id
        ? await dispatch(updateUser({ id, data: mappedData }))
        : await dispatch(createUser(mappedData));

      if (
        updateUser.rejected.match(resultAction) ||
        createUser.rejected.match(resultAction)
      ) {
        const error = resultAction.payload as ApiError;
        console.log(error)
        setFormError(error);
        // setFields(fields.map((item:any)=>{
        //   let data :any=error;
        //   data = data?.validationErrors[item.name]
        //   item.validationError = data?.message ?? ''
        //   return item;
        // }))
        notification.showError(error.message || "Erro ao salvar dados");
        return;
      }

      notification.showSuccess("Dados salvos com sucesso!");
      navigate("/users");
    } catch (error) {
      console.error("❌ UserForm - Erro inesperado:", error);
      notification.showError("Erro inesperado ao salvar os dados");
      setFormError({
        code: "UNKNOWN_ERROR",
        message: "Erro inesperado ao salvar os dados",
        statusCode: 500,
      });
    } finally {
      setIsSubmitting(false);
      submitAttemptRef.current = false;
    }
  };

  // Return nothing if user is a lawyer (will be redirected)
  if (currentUserRole === UserRole.LAWYER) {
    return null;
  }

  if (isInitialLoad && (loading || affiliatesLoading)) {
    return <FormSkeleton fields={6} />;
  }

  return (
    <>
    {/* <Box sx={{ p: 3 }}> */}
     {/*  <Typography variant="h5" gutterBottom>
        {id ? "Editar Usuário" : "Novo Usuário"}
      </Typography>
       */}
      {/* // {currentUserRole === UserRole.MANAGER && ( */}
      {/* //   <Alert severity="info" sx={{ mb: 3 }}>
      //     Como gerente, você não pode criar usuários administradores nem alterar a empresa vinculada ao usuário.
      //   </Alert>
      // )} */}
      
      <DataForm
        data={formData}
        setFormDataParent={setFormData}
        fields={fields}
        isEdit={id!=undefined}

        onSubmit={handleSubmit}
        basePath="users"
        title={id ? "Editar Colaborador" : "Novo Colaborador"}
        error={formError}
        onClearError={() => setFormError(null)}
        disabled={isSubmitting}
      />
      {/* </Box> */}
    {/* // 
    // </div> */}
    </>

  );
};

export default UserForm;
